/**
 * The file's own sample rate, read from its header. The browser decodes everything to the device's output rate
 * (often 48 kHz), so asking it would show the wrong number for a 44.1 kHz file. Returns null for formats we do
 * not read (the caller then leaves the rate out rather than guess).
 */
const MP3_RATES: Record<number, number[]> = { 3: [44100, 48000, 32000], 2: [22050, 24000, 16000], 0: [11025, 12000, 8000] };

export function sniffSampleRate(bytes: Uint8Array): number | null {
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const tag = (o: number, s: string) => bytes.length >= o + s.length && s.split("").every((ch, i) => bytes[o + i] === ch.charCodeAt(0));
  if (tag(0, "RIFF") && tag(8, "WAVE")) {
    let o = 12;
    while (o + 8 <= bytes.length) {
      const size = dv.getUint32(o + 4, true);
      if (tag(o, "fmt ") && o + 16 <= bytes.length) return dv.getUint32(o + 12, true) || null;
      o += 8 + size + (size & 1);
    }
    return null;
  }
  if (tag(0, "fLaC") && bytes.length >= 22) return ((bytes[18] << 12) | (bytes[19] << 4) | (bytes[20] >> 4)) || null;
  if (tag(0, "OggS")) {
    const i = Array.from({ length: Math.min(bytes.length - 16, 400) }, (_, k) => k).find((k) => bytes[k] === 1 && tag(k + 1, "vorbis"));
    if (i !== undefined && i + 15 <= bytes.length) return dv.getUint32(i + 12, true) || null;
    return null; // Opus stores no playback rate we can quote
  }
  // MP3: optional ID3v2 tag, then the first frame header.
  let o = 0;
  if (tag(0, "ID3") && bytes.length > 10) o = 10 + ((bytes[6] & 0x7f) << 21 | (bytes[7] & 0x7f) << 14 | (bytes[8] & 0x7f) << 7 | (bytes[9] & 0x7f));
  for (let k = o; k < Math.min(bytes.length - 4, o + 4096); k++) {
    if (bytes[k] === 0xff && (bytes[k + 1] & 0xe0) === 0xe0) {
      const ver = (bytes[k + 1] >> 3) & 3, layer = (bytes[k + 1] >> 1) & 3, idx = (bytes[k + 2] >> 2) & 3;
      if (ver !== 1 && layer !== 0 && idx !== 3 && MP3_RATES[ver]) return MP3_RATES[ver][idx];
    }
  }
  return null;
}
